import React, { useEffect, useState } from 'react';
import { resumeService } from '../services/api';
import type { Resume } from '../types';
import { FileText, Loader2, Eye } from 'lucide-react';
import { augmentPreviewHtml } from '../utils/previewHtml';

interface Props {
  resume: Resume;
  onOpen: () => void;
  onPreview: () => void;
}

const ResumeCardPreview: React.FC<Props> = ({ resume, onOpen, onPreview }) => {
  const [html, setHtml] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    resumeService
      .previewSavedResume(resume as unknown as Resume)
      .then((response) => {
        if (!cancelled) setHtml(augmentPreviewHtml(response.data));
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [resume]);

  return (
    <div
      className="relative aspect-[1/1.414] bg-white overflow-hidden group/preview cursor-pointer"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      aria-label={`Open ${resume.personalInfo.fullName} in editor`}
    >
      {html ? (
        <iframe
          title={`${resume.personalInfo.fullName} preview`}
          srcDoc={html}
          sandbox="allow-same-origin"
          className="absolute inset-0 w-full h-full border-none pointer-events-none"
          tabIndex={-1}
          aria-hidden
          scrolling="no"
        />
      ) : failed ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-zinc-300">
          <FileText className="w-8 h-8" />
          <span className="text-xs">Preview unavailable</span>
        </div>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-zinc-300">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      )}

      <div className="absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-white via-white/70 to-transparent pointer-events-none" />

      <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover/preview:bg-black/5 opacity-0 group-hover/preview:opacity-100 transition-all duration-300 pointer-events-none">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onPreview();
          }}
          className="h-8 px-3 bg-white text-foreground text-xs font-medium rounded-btn shadow-soft hover:bg-zinc-50 transition-colors inline-flex items-center gap-1.5 pointer-events-auto cursor-pointer"
          aria-label={`Preview ${resume.personalInfo.fullName}`}
        >
          <Eye className="w-3.5 h-3.5" />
          Preview
        </button>
      </div>
    </div>
  );
};

export default ResumeCardPreview;
